import { useApiClient } from './useApiClient'
import type { AuthResponse, AuthTokenParams } from '~/types/auth'
import type { UsuarioResponse, UsuarioPaginadoResponse } from '~/types/gerencia'
import { useAuthStorage } from './useAuthStorage'
import { useRuntimeConfig } from '#app'

export function useNtaskApi() {
  const config = useRuntimeConfig()
  const baseURL = String(config.public.baseNtaskURL)
  const api = useApiClient({
    baseURL,
    tokenKey: 'auth_token',
    onUnauthorized: () => {
      // Redirecionar para login se necessário
      // await navigateTo('/login')
    }
  })

  const { saveAuthData, clearAuthData } = useAuthStorage()

  const auth = {
    login: (credentials: { email: string; password: string }) =>
      api.post('/auth/login', credentials),
    
    logout: () => {
      clearAuthData()
      return api.post('/auth/logout', {})
    },

    authenticateWithToken: async (token: string): Promise<AuthResponse> => {
      try {
        const response = await api.post('/autenticacao/ping', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }) as AuthResponse
        
        saveAuthData(response)
        
        return response
      } catch (error) {
        console.error('Erro na autenticação com token:', error)
        throw error
      }
    },

    processTokenFromUrl: async (): Promise<AuthResponse | null> => {
      if (process.client) {
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')
        
        if (token) {
          try {
            return await auth.authenticateWithToken(token)
          } catch (error) {
            console.error('Erro ao processar token da URL:', error)
            return null
          }
        }
      }
      return null
    }
  }

  const gerencia = {
    getUsuario: (): Promise<UsuarioResponse> =>
      api.get('/gerencia/usuario/lista-por-modulo/controle_processos') as Promise<UsuarioResponse>,
    
    buscarUsuarios: (busca: string): Promise<UsuarioPaginadoResponse> =>
      api.get(`/gerencia/usuarios/controle_processos?busca=${encodeURIComponent(busca)}`) as Promise<UsuarioPaginadoResponse>,
    
    salvarPermissao: (idPermissao: string, permissoes: {
      inserir: boolean;
      editar: boolean;
      excluir: boolean;
      visualizar: boolean;
    }) => {
      const { getCurrentCartorio } = useAuthStorage()
      const cartorioAtual = getCurrentCartorio()
      
      const payload = {
        ...permissoes,
        cartorio_id: cartorioAtual?.id || null
      }
      
      return api.put(`/gerencia/usuario/permissao/${idPermissao}`, payload)
    },

    criarPermissao: (usuarioId: string, permissoes: {
      inserir: boolean;
      editar: boolean;
      excluir: boolean;
      visualizar: boolean;
    }) => {
      const { getCurrentCartorio } = useAuthStorage()
      const cartorioAtual = getCurrentCartorio()
      
      const payload = {
        ...permissoes,
        cartorio_id: cartorioAtual?.id || null
      }
      
      return api.post(`/gerencia/usuario/${usuarioId}/permissao/controle_processos`, payload)
    }
  }

  const arquivos = {
    getArquivo: (arquivo: string) =>
      api.get(`/arquivos/${arquivo}`, {
        responseType: 'blob'
      })
  }

  return {
    ...api,
    auth,
    gerencia,
    arquivos,
  }
} 